import { useState } from 'react';
import { motion } from 'framer-motion';
import { CoverSlideData, Theme, slideTypographyStyle } from '../slideTypes';
import { SettingsButton, SlideSettingsDrawer, DrawerSection, DrawerOption, DrawerCheckbox, DrawerRemove } from '../shared/SlideShared';
import { ImageDropZone } from '../ImageDropZone';
import { FocalPointPicker, focalPos } from '../FocalPointPicker';
import { UnsplashCredit, withImageAttribution } from '../shared/UnsplashCredit';
import { springs } from '../../../ui/slide-animations';
import { WrappingInput } from '../shared/WrappingInput';

export function CoverSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: CoverSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: CoverSlideData) => void;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const layoutLabels: Record<CoverSlideData['layout'], string> = {
    'centered': 'Centered',
    'left-aligned': 'Left Aligned',
    'stacked': 'Stacked',
  };

  const settingsDrawer = isEditing && (
    <SlideSettingsDrawer
      isOpen={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      title="Cover Page Settings"
    >
      <DrawerSection label="Layout">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {(Object.keys(layoutLabels) as CoverSlideData['layout'][]).map((l) => (
            <DrawerOption key={l} selected={data.layout === l} onClick={() => onUpdate({ ...data, layout: l })}>
              {layoutLabels[l]}
            </DrawerOption>
          ))}
        </div>
      </DrawerSection>
      <DrawerSection label="Logo">
        <ImageDropZone
          compact
          onImageUrl={(url) => onUpdate({ ...data, logoUrl: url })}
          currentUrl={data.logoUrl || undefined}
          primaryColor="var(--accent)"
        />
        {data.logoUrl && (
          <DrawerRemove onClick={() => onUpdate({ ...data, logoUrl: '' })}>Remove logo</DrawerRemove>
        )}
      </DrawerSection>
      <DrawerSection label="Background image">
        <ImageDropZone
          compact
          onImageUrl={(url, attribution) =>
            onUpdate(withImageAttribution({ ...data, backgroundImage: url }, attribution))
          }
          currentUrl={data.backgroundImage || undefined}
          primaryColor="var(--accent)"
        />
        {data.backgroundImage && (
          <DrawerRemove
            onClick={() => onUpdate(withImageAttribution({ ...data, backgroundImage: undefined }, undefined))}
          >
            Remove background
          </DrawerRemove>
        )}
        {data.backgroundImage && (
          <div style={{ marginTop: 10 }}>
            <DrawerSection label="Focal point">
              <FocalPointPicker
                imageUrl={data.backgroundImage}
                value={data.focalPoint}
                onChange={(fp) => onUpdate({ ...data, focalPoint: fp })}
              />
            </DrawerSection>
          </div>
        )}
      </DrawerSection>
      <DrawerCheckbox
        id="cover-accent-bar"
        checked={data.showAccentBar}
        onChange={(v) => onUpdate({ ...data, showAccentBar: v })}
        label="Show accent bar"
      />
      {data.backgroundImage && (
        <DrawerCheckbox
          id="cover-dark-overlay"
          checked={data.darkOverlay}
          onChange={(v) => onUpdate({ ...data, darkOverlay: v })}
          label="Dark overlay on background"
        />
      )}
    </SlideSettingsDrawer>
  );

  // Determine alignment / layout classes
  const isCentered = data.layout === 'centered';
  const isStacked = data.layout === 'stacked';
  const alignClass = isCentered ? 'text-center items-center' : 'text-left items-start';

  // Resolve text colors (invert when dark overlay active)
  const hasDarkBg = data.backgroundImage && data.darkOverlay;
  const titleColor = hasDarkBg ? '#ffffff' : theme.colors.text;
  const subtitleColor = hasDarkBg ? 'rgba(255,255,255,0.7)' : theme.colors.textMuted;
  const accentColor = theme.colors.primary;
  // Cover slide always uses a serif title — override modern-dark's sans-serif heading
  const coverTitleFont = theme.id === 'modern-dark'
    ? '"DM Serif Display", Georgia, serif'
    : theme.fonts.heading;
  const coverTitleWeight = 400;
  // Use tenant logo if available, otherwise leave blank
  const effectiveLogoUrl = data.logoUrl || '';

  // ── Edit mode ──
  if (isEditing) {
    return (
      <div
        className={`relative h-full group flex flex-col justify-center ${alignClass}`}
        style={{
          padding: theme.spacing.padded,
          backgroundColor: theme.colors.background,
          backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: focalPos(data.focalPoint),
        }}
      >
        {data.backgroundImage && data.darkOverlay && (
          <div className="absolute inset-0 bg-black/50 pointer-events-none" />
        )}
        <div className={`relative z-10 flex ${isStacked ? 'flex-col gap-8' : isCentered ? 'flex-col gap-6' : 'flex-col gap-6'} ${alignClass} slide-content-wide ${isCentered ? 'mx-auto' : ''}`}>
          {/* Logo preview */}
          {effectiveLogoUrl && (
            <div className={`${isCentered ? 'mx-auto' : ''}`}>
              <img
                src={effectiveLogoUrl}
                alt="Logo"
                className="max-h-16 w-auto object-contain"
                style={{ filter: hasDarkBg ? 'brightness(1.1)' : undefined }}
              />
            </div>
          )}
          {/* Title input */}
          <WrappingInput
            value={data.title}
            onChange={(title) => onUpdate({ ...data, title })}
            placeholder="Course Title..."
            className="bg-transparent border-none outline-none w-full"
            data-slide-level="title"
            style={{
              ...slideTypographyStyle(theme, 'title', coverTitleFont),
              fontWeight: coverTitleWeight,
              color: titleColor,
              textAlign: isCentered ? 'center' : 'left',
            }}
          />
          {/* Accent bar preview */}
          {data.showAccentBar && (
            <div
              className={`h-1 rounded-full ${isCentered ? 'mx-auto' : ''}`}
              style={{ backgroundColor: accentColor, width: isCentered ? '4rem' : '5rem' }}
            />
          )}
          {/* Subtitle input */}
          <WrappingInput
            value={data.subtitle || ''}
            onChange={(subtitle) => onUpdate({ ...data, subtitle })}
            placeholder="Subtitle (optional)..."
            className="bg-transparent border-none outline-none w-full"
            style={{
              ...slideTypographyStyle(theme, 'subheading', theme.fonts.body),
              color: subtitleColor,
              textAlign: isCentered ? 'center' : 'left',
            }}
          />
        </div>
        <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
        <SettingsButton onClick={() => setSettingsOpen(true)} />
        {settingsDrawer}
      </div>
    );
  }

  // ── View / Presentation mode ──
  // Stacked layout: logo top, large title in center, subtitle bottom
  if (isStacked) {
    return (
      <div
        className="relative h-full flex flex-col justify-between overflow-hidden"
        style={{
          backgroundColor: theme.colors.background,
          backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: focalPos(data.focalPoint),
        }}
      >
        {data.backgroundImage && data.darkOverlay && (
          <div className="absolute inset-0 bg-black/50 pointer-events-none" />
        )}
        {/* Decorative corner accent */}
        <motion.div
          className="absolute top-0 left-0 pointer-events-none"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.08, scale: 1 }}
          transition={{ ...(springs.smooth as object), delay: 0.5 }}
          style={{
            width: '15cqi',
            height: '15cqi',
            background: `radial-gradient(circle at top left, ${accentColor}, transparent 70%)`,
          }}
        />
        <div className="relative z-10 flex flex-col justify-between h-full" style={{ padding: theme.spacing.padded }}>
          {/* Top: logo */}
          <motion.div
            className="pt-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...(springs.gentle as object), delay: 0.1 }}
          >
            {effectiveLogoUrl && (
              <img
                src={effectiveLogoUrl}
                alt="Logo"
                className="max-h-14 w-auto object-contain"
                style={{ filter: hasDarkBg ? 'brightness(1.1)' : undefined }}
              />
            )}
          </motion.div>
          {/* Center: title + accent */}
          <div className="flex-1 flex flex-col justify-center slide-content-wide">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springs.gentle as object}
              data-slide-level="title"
              style={{
                ...slideTypographyStyle(theme, 'title', coverTitleFont),
                fontWeight: coverTitleWeight,
                color: titleColor,
                margin: 0,
              }}
            >
              {data.title}
            </motion.h1>
            {data.showAccentBar && (
              <motion.div
                className="mt-5 mb-5 h-1 rounded-full"
                style={{ backgroundColor: accentColor, width: 'var(--slide-accent-width)' }}
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ ...(springs.decorative as object), delay: 0.2 }}
              />
            )}
            {data.subtitle && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...(springs.gentle as object), delay: 0.3 }}
                style={{
                  ...slideTypographyStyle(theme, 'subheading', theme.fonts.body),
                  color: subtitleColor,
                  margin: 0,
                }}
              >
                {data.subtitle}
              </motion.p>
            )}
          </div>
          {/* Bottom: decorative accent line */}
          <motion.div
            className="pb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...(springs.smooth as object), delay: 0.6 }}
          >
            <div
              className="h-px w-full"
              style={{ backgroundColor: accentColor, opacity: 0.2 }}
            />
          </motion.div>
        </div>
        <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
      </div>
    );
  }

  // Centered + Left-aligned layouts
  return (
    <div
      className="relative h-full flex flex-col overflow-hidden"
      style={{
        backgroundColor: theme.colors.background,
        backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: focalPos(data.focalPoint),
      }}
    >
      {data.backgroundImage && data.darkOverlay && (
        <div className="absolute inset-0 bg-black/50 pointer-events-none" />
      )}
      {/* Subtle gradient atmosphere when no background image */}
      {!data.backgroundImage && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at ${isCentered ? '50% 40%' : '20% 50%'}, ${accentColor}08 0%, transparent 70%)`,
          }}
        />
      )}
      <div className={`relative z-10 h-full flex flex-col justify-center ${alignClass}`} style={{ padding: theme.spacing.padded }}>
        <div className={`slide-content-wide ${isCentered ? 'mx-auto' : ''} flex flex-col ${alignClass} gap-2`}>
          {/* Logo */}
          {effectiveLogoUrl && (
            <motion.div
              className={`mb-6 ${isCentered ? 'mx-auto' : ''}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...(springs.gentle as object), delay: 0.05 }}
            >
              <img
                src={effectiveLogoUrl}
                alt="Logo"
                className="max-h-16 w-auto object-contain"
                style={{ filter: hasDarkBg ? 'brightness(1.1)' : undefined }}
              />
            </motion.div>
          )}
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springs.gentle as object}
            data-slide-level="title"
            style={{
              ...slideTypographyStyle(theme, 'title', coverTitleFont),
              fontWeight: coverTitleWeight,
              color: titleColor,
              margin: 0,
            }}
          >
            {data.title}
          </motion.h1>
          {/* Accent bar */}
          {data.showAccentBar && (
            <motion.div
              className={`mt-5 mb-5 h-1 rounded-full ${isCentered ? '' : ''}`}
              style={{
                backgroundColor: accentColor,
                width: isCentered ? '4rem' : '5rem',
              }}
              initial={{ scaleX: 0, originX: isCentered ? 0.5 : 0 }}
              animate={{ scaleX: 1 }}
              transition={{ ...(springs.decorative as object), delay: 0.15 }}
            />
          )}
          {/* Subtitle */}
          {data.subtitle && (
            <motion.p
              className={`slide-content-narrow ${isCentered ? 'mx-auto' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...(springs.gentle as object), delay: 0.25 }}
              style={{
                ...slideTypographyStyle(theme, 'subheading', theme.fonts.body),
                color: subtitleColor,
                margin: 0,
              }}
            >
              {data.subtitle}
            </motion.p>
          )}
        </div>
      </div>
      {/* Bottom accent line */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ backgroundColor: accentColor }}
        initial={{ scaleX: 0, originX: isCentered ? 0.5 : 0 }}
        animate={{ scaleX: 1 }}
        transition={{ ...(springs.smooth as object), delay: 0.4 }}
      />
      <UnsplashCredit author={data.unsplashAuthor} authorUrl={data.unsplashAuthorUrl} />
    </div>
  );
}
